import { homeLessonDoneToday } from '../Sparks/earnedToday';
import { isPracticeExamTopic, visibleLessons } from './lessonGroups';

/**
 * Picks one Home Learning lesson to suggest, from the Home Learning rates (`rates`: lessonId → { band,
 * bandPercent, yearOffset }). The first rule that finds a lesson wins:
 *
 *   1. fix:     a red, then amber, lesson in the child's year (yearOffset 0), lowest score first.
 *   2. new:     an untried lesson in the child's year, topics the child has started first, then topic and
 *               lesson order.
 *   3. stretch: an untried lesson one year above (yearOffset −1), in topic and lesson order.
 *
 * Practice exams and lessons already done today (they earn no Sparks) are never suggested. Returns
 * { topic, lesson, reason } with reason 'red' | 'amber' | 'new' | 'stretch', or null (no rates, or nothing fits).
 */
export function recommendLesson(topics, rates, earnedToday) {
  if (!rates) return null;
  const candidates = [];
  topics.filter((topic) => !isPracticeExamTopic(topic)).forEach((topic, topicIndex) => {
    const lessons = visibleLessons(topic);
    const started = lessons.some((lesson) => (rates[lesson.lessonId._id]?.band ?? 'grey') !== 'grey');
    lessons.forEach((lesson, lessonIndex) => {
      const rate = rates[lesson.lessonId._id];
      if (!rate || homeLessonDoneToday(earnedToday, lesson.lessonId._id)) return;
      candidates.push({ topic, lesson, rate, started, topicIndex, lessonIndex });
    });
  });

  const inOrder = (a, b) => a.topicIndex - b.topicIndex || a.lessonIndex - b.lessonIndex;
  const pick = (filter, compare) => candidates.filter(filter).sort(compare)[0] ?? null;

  const fix = pick(
    (c) => c.rate.yearOffset === 0 && (c.rate.band === 'red' || c.rate.band === 'amber'),
    (a, b) => (a.rate.band === b.rate.band ? 0 : a.rate.band === 'red' ? -1 : 1) || (a.rate.bandPercent ?? 0) - (b.rate.bandPercent ?? 0) || inOrder(a, b),
  );
  if (fix) return { topic: fix.topic, lesson: fix.lesson, reason: fix.rate.band };

  const fresh = pick((c) => c.rate.yearOffset === 0 && c.rate.band === 'grey', (a, b) => Number(b.started) - Number(a.started) || inOrder(a, b));
  if (fresh) return { topic: fresh.topic, lesson: fresh.lesson, reason: 'new' };

  const stretch = pick((c) => c.rate.yearOffset === -1 && c.rate.band === 'grey', inOrder);
  return stretch ? { topic: stretch.topic, lesson: stretch.lesson, reason: 'stretch' } : null;
}
